"use client";

import Link from "next/link";
import {
  RUNTIME_OFFER_PLACEMENT_HI_RASHI_CALCULATOR_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_RASHI_CALCULATOR_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import dynamic from "next/dynamic";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { CALCULATOR_FORM_COPY } from "@/components/calculator-form-copy";
import { OfferPlacement } from "@/components/OfferPlacement";
import { calculatorReportOffer } from "@/components/OfferPlacement.collect-inputs-card";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import type { RatingPromptProps } from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { reportJsError } from "@/lib/js-error-beacon";

const RASHI_REPORT_OFFER = {
  en: {
    headline: "Your Moon sign is only the surface — what is the full chart hiding?",
    subhead:
      "Your rashi is one graha. Get your full Brihat Kundali to read every graha, dasha, and upay together.",
  },
  hi: {
    headline: "Aapki rashi to bas shuruaat hai — poori kundali kya keh rahi hai?",
    subhead:
      "Rashi sirf ek graha hai. Apni poori Brihat Kundali se sabhi graha, dasha aur upay ek saath samjhein.",
  },
} as const;

const ResultRatingPrompt = dynamic<RatingPromptProps>(() => import("@/components/RatingPrompt"), {
  ssr: false,
  loading: () => null,
});

import {
  isKnownRashi,
  resolveRashi,
  resolveRashiInfo,
  type RashiInfo,
} from "@/lib/rashi-presentation";
import { NAKSHATRA_AKSHARA } from "@/lib/nakshatra-akshara.generated";
import { elementNameHi, nakshatraNameHi, planetNameHi } from "@/lib/vedic-names-hi";
import { rashifalReadingHref } from "@/lib/rashifal-reading-href";
import { ToolInputError, toolErrorMessage } from "@/lib/tool-error-message";
import { RashiResultLevers } from "./RashiResultLevers";
import {
  ResultReserve,
  ResultReserveError,
  ResultReserveStatus,
  returnToForm,
} from "@/components/tool-shell/ResultReserve";

// Lazy: the date-only mode (RashiDateOnlyMode) loads only when toggled, so the
// exact-mode document pays nothing for it. The reserved min-height keeps the
// form→mode swap from collapsing to 0px (rule 58).
const RashiDateOnlyMode = dynamic(
  () => import("./RashiDateOnlyMode").then((mod) => mod.RashiDateOnlyMode),
  {
    ssr: false,
    loading: () => <div className="mx-auto min-h-[320px] max-w-md" aria-hidden />,
  }
);
type ExactRashiRequest = KundaliRequest &
  Required<Pick<KundaliRequest, "birth_time" | "latitude" | "longitude">>;

/**
 * `exact` computes the full Moon result (rashi + nakshatra + pada) from the
 * exact birth instant; `date-only` is the rule-29 #5 manual mode that shows
 * only what the DATE determines, or asks. Two inputs to one question — one
 * page, one toggle, never a second URL (rule 66).
 */
type RashiMode = "exact" | "date-only";

// The form card's words (submit, time hint, input errors) and the reserve
// that repeats its error are governed Hinglish on /hi/, like the card's
// labels (components/calculator-form-copy.ts, c-forms-10).
const INPUT_ERRORS = CALCULATOR_FORM_COPY.rashi;

const RATING_PROMPT = {
  en: "Was this rashi result accurate?",
  hi: "क्या यह राशि परिणाम सही लगा?",
} as const;

const MODE_LABELS = {
  en: { exact: "With birth time (exact)", dateOnly: "Date only (no birth time)" },
  hi: { exact: "जन्म समय के साथ (सटीक)", dateOnly: "केवल जन्म तिथि से" },
} as const;

export default function RashiCalculatorClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);
  // Keep the submitted birth details so the report offer can carry them into
  // /free-kundali (no re-typing — the #1 leak is offer→cart friction).
  const [lastRequest, setLastRequest] = useState<KundaliRequest | null>(null);
  // Local state only — no query param, no hash (rule 66 expansion freeze).
  const [mode, setMode] = useState<RashiMode>("exact");
  // Counts accepted submits. Non-zero mounts the result reserve; each
  // increment re-anchors it (rule 58, components/tool-shell/ResultReserve).
  const [submitSequence, setSubmitSequence] = useState(0);
  const reserveRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const copy = INPUT_ERRORS[defaultLanguage];

  // Anchored in the SUBMIT commit (inside CLS's input window), never when the
  // answer lands ~2 s later: scrolling to the late result is what made the
  // push of the sections below count (0.20-0.27 lab, ~34% field breaches).
  useLayoutEffect(() => {
    if (submitSequence === 0 || !reserveRef.current) return;
    reserveRef.current.scrollIntoView({ behavior: "auto", block: "start" });
  }, [submitSequence]);

  async function handleSubmit(data: KundaliRequest) {
    let request: ExactRashiRequest;
    try {
      request = toExactRashiRequest(data, defaultLanguage);
    } catch (err) {
      // An input problem stays at the form; only an engine call opens the reserve.
      setError(rashiErrorMessage(err, defaultLanguage));
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setSubmitSequence((sequence) => sequence + 1);
    try {
      const response = await generateKundali({ ...request, skip_interpretation: true });
      setResult(response);
      setLastRequest(request);
      trackCalculatorUsed({
        calculator: "rashi-calculator",
        language: defaultLanguage,
        source_path: sourcePath(defaultLanguage),
        has_time: true,
      });
    } catch (err) {
      setError(rashiErrorMessage(err, defaultLanguage));
    } finally {
      setLoading(false);
    }
  }

  if (mode === "date-only") {
    return (
      <div className="sacred-bg">
        <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6 sm:pb-16">
          <RashiModeToggle mode={mode} language={defaultLanguage} onChange={setMode} />
          <RashiDateOnlyMode
            language={defaultLanguage}
            onSwitchToExactMode={() => setMode("exact")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sacred-bg">
      <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6 sm:pb-16">
        {/* The form stays mounted: unmounting it above an anchored reserve
            would pull the reserve up the page when the answer lands. */}
        <div ref={formRef} className="mx-auto max-w-md">
          <RashiModeToggle mode={mode} language={defaultLanguage} onChange={setMode} />
          <BirthDetailsForm
            rememberBirthDetails
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel={copy.submit}
            timeRequiredHint={copy.timeRequiredHint}
            error={error ?? undefined}
            language={defaultLanguage}
            analyticsToolName="rashi-calculator"
            analyticsPlacement="rashi_birth_details"
            requireBirthTime
            requireResolvedPlace
            collectName={false}
            showTrustStrip
          />
        </div>

        {submitSequence > 0 && (
          <ResultReserve ref={reserveRef} surface="rashi">
            {loading && <ResultReserveStatus label={copy.calculating} />}
            {!loading && !result && error && (
              <ResultReserveError
                message={error}
                editLabel={copy.editDetails}
                onEdit={() => returnToForm(formRef.current)}
              />
            )}
            {result && (
              <div className="mt-8">
                <RashiResultExperience
                  result={result}
                  lastRequest={lastRequest}
                  language={defaultLanguage}
                  onReset={() => {
                    setResult(null);
                    setError(null);
                    returnToForm(formRef.current);
                  }}
                />
              </div>
            )}
          </ResultReserve>
        )}
      </div>
    </div>
  );
}

function RashiModeToggle({
  mode,
  language,
  onChange,
}: {
  mode: RashiMode;
  language: "en" | "hi";
  onChange: (mode: RashiMode) => void;
}) {
  const labels = MODE_LABELS[language];
  const options: { value: RashiMode; label: string }[] = [
    { value: "exact", label: labels.exact },
    { value: "date-only", label: labels.dateOnly },
  ];
  return (
    <div className="mx-auto mb-4 flex max-w-md gap-2" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={mode === option.value}
          // 44 px targets (c-forms-09). The active mode is a quiet filled
          // segment: a terracotta outline read as the page's primary action
          // and drew dead taps on the option already chosen (b-calc-11).
          className={`min-h-11 flex-1 rounded-full border px-3 py-2 text-body-small ${
            mode === option.value
              ? "border-border-interactive bg-surface-brand-subtle font-semibold text-text-primary"
              : "border-border-subtle bg-surface-raised text-text-secondary hover:bg-surface-pressed"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Hindi seekers get Hindi copy, never the raw English transport/API message.
/** @internal */
export function rashiErrorMessage(err: unknown, language: "en" | "hi"): string {
  return toolErrorMessage(err, language, INPUT_ERRORS[language].failed);
}

/** @internal */
export function toExactRashiRequest(
  data: KundaliRequest,
  language: "en" | "hi" = "en"
): ExactRashiRequest {
  if (!data.birth_time) {
    throw new ToolInputError(INPUT_ERRORS[language].timeRequired);
  }
  if (data.latitude == null || data.longitude == null) {
    throw new ToolInputError(INPUT_ERRORS[language].placeRequired);
  }
  return {
    ...data,
    birth_time: data.birth_time,
    latitude: data.latitude,
    longitude: data.longitude,
  };
}

function RashiResultExperience({
  result,
  lastRequest,
  language,
  onReset,
}: {
  result: KundaliResponse;
  lastRequest: KundaliRequest | null;
  language: "en" | "hi";
  onReset: () => void;
}) {
  const rashiInfo = resolveRashiInfo(result.moon_rashi);
  // Two DIFFERENT questions that a single nullable lookup used to answer at once:
  //
  //  • ENRICHMENT IS NOT ELIGIBILITY (rule 69, generalized to rendering).
  //    `rashiInfo` supplies cosmetic labels — symbol, ruling planet, element. Its
  //    absence used to `return` early and short-circuit the paid offer, so a
  //    missing decoration withheld monetization on this whole surface.
  //  • AN UNREADABLE ENGINE RESULT IS NOT SELLABLE (rules 29/44 — the engine is
  //    the author of truth). If `moon_rashi` names no rashi at all, we have just
  //    told the seeker we could not read their chart and emitted a contract
  //    error; offering them a ₹199 report built on that same result contradicts
  //    the message and monetizes a known-bad computation.
  //
  // So the offer follows the CONTRACT, never the decorations.
  const engineResultIsReadable = isKnownRashi(result.moon_rashi);
  return (
    <>
      {engineResultIsReadable ? (
        <RashiResult result={result} rashiInfo={rashiInfo} language={language} />
      ) : (
        <UnknownRashiResult moonRashi={result.moon_rashi} language={language} onReset={onReset} />
      )}
      {/* Intent-matched offer at the result moment (rules 39/40/46). */}
      {engineResultIsReadable ? (
        <OfferPlacement
          {...calculatorReportOffer({
            planId: "brihat_kundali",
            sourcePath: sourcePath(language),
            placement: offerPlacement(language),
            customerProblem: "rashi",
            language: language,
            birth: {
              name: lastRequest?.name,
              birthDate: lastRequest?.birth_date,
              birthTime: lastRequest?.birth_time ?? undefined,
              birthPlace: lastRequest?.birth_place,
            },
          })}
          headline={RASHI_REPORT_OFFER[language].headline}
          subhead={RASHI_REPORT_OFFER[language].subhead}
        />
      ) : null}
      {/* Recovery levers (2026-10-27): strong-consent opt-in + chat bridge for
          THIS reading, after the answer and the single offer (rule 39). */}
      {engineResultIsReadable ? (
        <RashiResultLevers result={result} pada={moonPadaOf(result)} language={language} />
      ) : null}
      <button
        type="button"
        onClick={onReset}
        className="mx-auto mt-4 block min-h-11 rounded-lg border border-ish-border px-6 py-3 text-center text-body font-semibold text-ish-text-muted transition-colors hover:text-ish-dark"
      >
        {language === "hi" ? "फिर से गणना करें" : "Calculate Again"}
      </button>
      <div className="mt-6">
        <ResultRatingPrompt
          source={"tool_result" as never}
          mode="thumbs+reasons"
          reasons={RESULT_REASONS}
          prompt={RATING_PROMPT[language]}
          language={language}
          context={{
            surface: "rashi_calculator_result",
            path: typeof window !== "undefined" ? window.location.pathname : null,
          }}
        />
      </div>
    </>
  );
}

function UnknownRashiResult({
  moonRashi,
  language,
  onReset,
}: {
  moonRashi?: string;
  language: "en" | "hi";
  onReset: () => void;
}) {
  useEffect(() => {
    reportJsError({
      error_name: "RashiResultContractError",
      error_message: `Unknown moon_rashi returned by Kundali API: ${moonRashi || "missing"}`,
      boundary: "rashi_calculator_result",
    });
  }, [moonRashi]);

  return (
    <div className="card-sacred p-6 text-center">
      <p className="text-sm text-ish-text-muted">
        {language === "hi"
          ? "यह चंद्र राशि परिणाम पढ़ा नहीं जा सका। कृपया फिर से गणना करें।"
          : "We could not read this Moon-sign result. Please calculate again."}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-4 min-h-11 rounded-lg border border-ish-border px-6 py-3 text-sm font-semibold text-ish-text-muted"
      >
        {language === "hi" ? "फिर से गणना करें" : "Calculate Again"}
      </button>
    </div>
  );
}

function RashiResult({
  result,
  rashiInfo,
  language,
}: {
  result: KundaliResponse;
  rashiInfo?: RashiInfo;
  language: "en" | "hi";
}) {
  const moonPada = moonPadaOf(result);
  const view =
    language === "hi" ? hindiResultView(result, rashiInfo) : englishResultView(result, rashiInfo);
  return (
    <div className="space-y-6">
      <div className="card-sacred p-6 sm:p-8 text-center">
        <span className="text-5xl block mb-3">{rashiInfo?.symbol ?? "\u2728"}</span>
        <h2
          className="text-[28px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {view.heading}
        </h2>
        {view.subheading ? (
          <p className="text-[18px] mt-1 text-ish-text-muted">{view.subheading}</p>
        ) : null}
        <div className="divider-warm my-5" />
        <ResultFactsHeading language={language} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <ResultFact label={language === "hi" ? "तत्व" : "Element"} value={view.element} />
          <ResultFact label={language === "hi" ? "स्वामी ग्रह" : "Ruler"} value={view.ruler} />
          <ResultFact
            label={language === "hi" ? "नक्षत्र" : "Nakshatra"}
            value={view.nakshatra}
            className={view.nakshatraClassName}
          />
          <ResultFact
            label={language === "hi" ? "नक्षत्र पद" : "Nakshatra Pada"}
            value={String(moonPada)}
          />
        </div>
      </div>
      <RashiNextSteps result={result} pada={moonPada} language={language} />
    </div>
  );
}

function moonPadaOf(result: KundaliResponse): number | string {
  return result.planets.find((planet) => planet.planet_id === "moon")?.nakshatra_pada ?? "-";
}

type RashiResultView = {
  heading: string;
  subheading?: string;
  element: string;
  ruler: string;
  nakshatra: string;
  nakshatraClassName: string;
};

function englishResultView(result: KundaliResponse, rashiInfo?: RashiInfo): RashiResultView {
  return {
    heading: result.moon_rashi,
    subheading: rashiInfo?.hi,
    element: rashiInfo?.element ?? "-",
    ruler: rashiInfo?.ruler ?? "-",
    nakshatra: result.moon_nakshatra.replace(/_/g, " "),
    nakshatraClassName: "capitalize",
  };
}

// Hindi names first, the engine's Latin rashi id as the gloss; any id the
// Hindi tables don't know falls back to the English view rather than "-".
function hindiResultView(result: KundaliResponse, rashiInfo?: RashiInfo): RashiResultView {
  const english = englishResultView(result, rashiInfo);
  const nakshatra = nakshatraNameHi(result.moon_nakshatra);
  return {
    heading: rashiInfo?.hi ?? english.heading,
    subheading: result.moon_rashi,
    element: elementNameHi(rashiInfo?.element) ?? english.element,
    ruler: planetNameHi(rashiInfo?.ruler) ?? english.ruler,
    nakshatra: nakshatra ?? english.nakshatra,
    nakshatraClassName: nakshatra ? "" : english.nakshatraClassName,
  };
}

function ResultFactsHeading({ language }: { language: "en" | "hi" }) {
  return (
    <h3 className="mb-3 text-body-small font-semibold text-text-secondary">
      {language === "hi" ? "राशि · नक्षत्र · पद" : "Rashi · Nakshatra · Pada"}
    </h3>
  );
}

const squashName = (name: string) => name.toLowerCase().replace(/[^a-z]/g, "");

/**
 * The pada's naming syllable (naam akshar), when the nakshatra and pada are known.
 * @internal
 */
export function namingSyllable(nakshatra: string, pada: number | string): string | null {
  const padaNumber = Number(pada);
  if (!Number.isInteger(padaNumber) || padaNumber < 1 || padaNumber > 4) return null;
  const key = squashName(nakshatra.replace(/_/g, " "));
  const row = NAKSHATRA_AKSHARA.find(
    (entry) =>
      squashName(entry.nakshatra) === key || (key === "moola" && entry.nakshatra === "Mula")
  );
  return row?.syllables[padaNumber - 1] ?? null;
}

/** What to do with the result: the naming syllable and today's rashifal for the sign. */
function RashiNextSteps({
  result,
  pada,
  language,
}: {
  result: KundaliResponse;
  pada: number | string;
  language: "en" | "hi";
}) {
  const hi = language === "hi";
  const nakshatraName =
    (hi && nakshatraNameHi(result.moon_nakshatra)) || result.moon_nakshatra.replace(/_/g, " ");
  const syllable = namingSyllable(result.moon_nakshatra, pada);
  const rashi = resolveRashi(result.moon_rashi);
  if (!syllable && !rashi) return null;
  return (
    <div className="card-sacred space-y-3 p-5 text-left" data-rashi-next-steps>
      {syllable ? (
        <p className="text-body text-text-primary">
          {hi
            ? `नाम अक्षर (${nakshatraName}, पद ${pada}): `
            : `Naming syllable (naam akshar) for ${nakshatraName} pada ${pada}: `}
          <strong>{syllable}</strong>.{" "}
          <Link
            href={hi ? "/hi/baby-names/" : "/baby-names/"}
            className="inline font-semibold text-intent-brand underline"
          >
            {hi
              ? `"${syllable}" से शुरू होने वाले नाम देखें`
              : `Find names starting with "${syllable}"`}
          </Link>
        </p>
      ) : null}
      {rashi ? (
        <p className="text-body text-text-primary">
          <Link
            href={rashifalReadingHref(rashi.id, hi ? "hi" : "en")}
            className="inline font-semibold text-intent-brand underline"
          >
            {hi
              ? `${rashi.name_hi} राशि का आज का राशिफल`
              : `Today's rashifal for ${rashi.name_en} (${rashi.name_sa})`}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function sourcePath(language: "en" | "hi") {
  return language === "hi" ? "/hi/rashi-calculator" : "/rashi-calculator";
}

function offerPlacement(language: "en" | "hi") {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_RASHI_CALCULATOR_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_RASHI_CALCULATOR_REPORT_BRIDGE;
}

function ResultFact({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div>
      <span className="block text-caption mb-1 text-ish-text-faint">{label}</span>
      <span className={`font-semibold text-body text-ish-dark ${className}`}>{value}</span>
    </div>
  );
}
